<template>
  <div>
    <section-header
      :breadcrumb-items="breadcrumb"
      class="pl-4 mb-5"
      button
      button-icon=""
      :button-title="buttonTitle"
      @click-button="buttonClick"
    >
      <template #button>
        <div class="d-inline-flex">
          <v-btn
            class="cancel-btn synthesis-text text-size-14 mr-3"
            depressed
            height="42"
            large
            @click="handleCancel"
          >
            {{ $t('Common.Button.Cancel') }}
          </v-btn>
          <v-btn
            :color="'synthesis-ui-green-01'"
            class="synthesis-text text-size-14 white--text"
            depressed
            height="42"
            large
            @click="buttonClick"
          >
            {{ buttonTitle }}
          </v-btn>
        </div>
      </template>
    </section-header>
    <v-tabs
      v-model="tab"
      active-class="active-tab"
      background-color="transparent"
      class="pl-4"
      height="30"
      hide-slider
      show-arrows
    >
      <v-tab>{{ $t('Assumptions.EditAssumptions.Tabs.LoanAssumptions.Title') }}</v-tab>
      <v-tab>{{ $t('Assumptions.EditAssumptions.Tabs.DepositAssumptions.Title') }}</v-tab>
      <v-tab>{{ $t('Assumptions.EditAssumptions.Tabs.GeneralAssumptions.Title') }}</v-tab>
      <v-tab>{{ $t('Assumptions.EditAssumptions.Tabs.LiquidityAssumptions.Title') }}</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <loan-assumptions
          v-if="loanSections.length > 0 && months.length > 0 && Object.keys(loanModel).length > 0"
          :loan-sections="loanSections"
          :months="months"
          :loan-model="loanModel"
          :reinvestment-model="loanReinvestmentModel"
          :reinvestment-sections="loanReinvestmentSections"
          @update-loan-rate="updateLoanRate"
          @update-reinvestment="onUpdateLoanReinvestment"
        />
      </v-tab-item>
      <v-tab-item>
        <deposit-assumptions
          v-if="depositSections.length > 0 && Object.keys(depositModel).length > 0"
          :deposit-sections="depositSections"
          :deposit-model="depositModel"
          :reinvestment-sections="depositReinvestmentSections"
          :reinvestment-model="depositReinvestmentModel"
          @update-deposit="onUpdateDeposit"
          @update-reinvestment="onUpdateDepositReinvestment"
        />
      </v-tab-item>
      <v-tab-item>
        <general-assumptions
          v-if="generalSections.length > 0 && Object.keys(generalModel).length > 0"
          :base-currency="baseCurrency"
          :sections="generalSections"
          :model="generalModel"
          @update-general="onUpdateGeneral"
        />
      </v-tab-item>
      <v-tab-item>
        <liquidity-assumptions
          v-if="Object.keys(liquidityModel).length > 0"
          :base-currency="baseCurrency"
          :model="liquidityModel"
          @update-liquidity="onUpdateLiquidity"
        />
      </v-tab-item>
    </v-tabs-items>

    <!-- Rerun Modal -->
    <rerun-modal
      :open="showRerunModal"
      :on-close="closeRerunModal"
      :title="$t('Assumptions.EditAssumptions.RerunModal.Title')"
      :subtitle="$t('Assumptions.EditAssumptions.RerunModal.Subtitle')"
      :progress="rerunProgress"
      :success="rerunSuccess"
      :success-title="$t('Assumptions.EditAssumptions.RerunModal.SuccessTitle')"
      :success-subtitle="$t('Assumptions.EditAssumptions.RerunModal.SuccessSubtitle')"
      :success-link-text="successLinkText"
      :on-success-link-click="onSuccessLinkClick"
    />
  </div>
</template>

  <script>
import API from '@/api/Api'
import LoanAssumptions from '@/theme/default/views/banking/risk/interest-rate/LoanAssumptions.vue'
import DepositAssumptions from '@/theme/default/views/banking/risk/interest-rate/DepositAssumptions.vue'
import GeneralAssumptions from '@/theme/default/views/banking/risk/interest-rate/GeneralAssumptions.vue'
import SectionHeader from '@/theme/default/components/common/SectionHeader.vue'
import RerunModal from '@/theme/default/components/common/RerunModal.vue'
import LiquidityAssumptions from '@/theme/default/views/banking/risk/liquidity/LiquidityAssumptions.vue'

export default {
  name      : 'EditAssumptions',
  components: {
    LiquidityAssumptions,
    LoanAssumptions,
    DepositAssumptions,
    GeneralAssumptions,
    SectionHeader,
    RerunModal
  },
  dataStore: {
    BankAssumptions             : 'Assumptions.BankAssumptions.Data',
    currentAnalysisId           : 'ActiveAnalysis.currentAnalysisId',
    previousAnalysisId          : 'ActiveAnalysis.previousAnalysisId',
    isDraftAnalysis             : 'ActiveAnalysis.isDraft',
    currentLiquidityParametersId: 'ActiveLiquidityParameters.currentLiquidityParametersId',
    isDraftLiquidity            : 'ActiveLiquidityParameters.isDraft'
  },
  props: {},
  data () {
    return {
      tab                     : 0,
      loanModel               : {},
      depositModel            : {},
      loanReinvestmentModel   : {},
      depositReinvestmentModel: {},
      generalModel            : {},
      liquidityModel          : {},
      showRerunModal          : false,
      rerunProgress           : 0,
      rerunSuccess            : false,
      reRunAnalysisId         : null,
      reRunLiquidityId        : null
    }
  },
  computed: {
    buttonTitle () {
      switch (this.tab) {
        case 0:
        case 1:
        case 2:
          return this.$t('Assumptions.EditAssumptions.InterestRateRerun')
        case 3:
          return this.$t('Assumptions.EditAssumptions.LiquidityRerun')
        default:
          return this.$t('Assumptions.EditAssumptions.Title')
      }
    },
    buttonClick () {
      switch (this.tab) {
        case 0:
        case 1:
        case 2:
          return this.rerunInterestRate
        case 3:
          return this.rerunLiquidity
        default:
          return this.rerunInterestRate
      }
    },
    baseCurrency () {
      return this.assumptions?.baseCurrency?.displaySymbol || ''
    },
    successLinkText () {
      switch (this.tab) {
        case 0:
        case 1:
        case 2:
          switch (this.$route.query.from) {
            case this.$t('EVE.Tab.InternalThresholds.Slug'):
              return this.$t('EVE.Tab.InternalThresholds.Title')
            case this.$t('EVE.Tab.BaselTests.Slug'):
              return this.$t('EVE.Tab.BaselTests.Title')
            case this.$t('EVE.Tab.HistoricalEVEChanges.Slug'):
              return this.$t('EVE.Tab.HistoricalEVEChanges.Title')
            case this.$t('EVE.Tab.SharpestEVEChanges.Slug'):
              return this.$t('EVE.Tab.SharpestEVEChanges.Title')
            case this.$t('NII.Tab.SynthesisRisk.Slug'):
              return this.$t('NII.Tab.SynthesisRisk.Title')
            case this.$t('NII.Tab.InternalThresholds.Slug'):
              return this.$t('NII.Tab.InternalThresholds.Title')
            case this.$t('EVE.Tab.RegulatoryThresholds.Slug'):
            default:
              return this.$t('EVE.Tab.RegulatoryThresholds.Title')
          }
        case 3:
          switch (this.$route.query.from) {
            case this.$t('RiskLiquidity.Tab.DeferenceReadiness.Slug'):
              return this.$t('RiskLiquidity.Tab.DeferenceReadiness.Title')
            case this.$t('RiskLiquidity.Tab.BankFailures.Slug'):
              return this.$t('RiskLiquidity.Tab.BankFailures.Title')
            case this.$t('RiskLiquidity.Tab.BankRunBenchmarking.Slug'):
              return this.$t('RiskLiquidity.Tab.BankRunBenchmarking.Title')
            case this.$t('RiskLiquidity.Tab.Monitoring.Slug'):
            default:
              return this.$t('RiskLiquidity.Tab.Monitoring.Title')
          }
        default:
          return this.$t('EVE.Tab.RegulatoryThresholds.Title')
      }
    },
    breadcrumb () {
      return [{
        text    : this.$t('InterestRate.HeaderTitle'),
        disabled: true
      }, {
        text    : this.$t('Assumptions.EditAssumptions.Title'),
        disabled: true
      }]
    },
    accountId () {
      return this.user?.selectedAccountId || null
    },
    assumptions () {
      return this.BankAssumptions?.Data?.analysisParameters || {}
    },
    liquidityAssumptions () {
      return this.BankAssumptions?.Data?.liquidityAssumptions || {}
    },
    months () {
      return this.assumptions?.loanPrepaymentRateMonths || [12, 24, 36, 48]
    },
    generalSections () {
      // order by category codes then map to UI-friendly structure
      const order = this.assumptions?.generalAssumptionsCategoryCodes || []
      const byCode = Object.fromEntries(
        (this.assumptions?.generalAssumptions || []).map(c => [c.categoryCode, c])
      )
      return order.map(code => {
        const c = byCode[code]
        if (!c) return null
        return {
          code  : c.categoryCode,
          label : c.categoryLabel,
          fields: (c.categoryFields || []).map(f => ({
            code       : f.code,
            label      : f.label,
            description: f.description,
            fieldType  : f.fieldType,
            editable   : f.editable
          }))
        }
      }).filter(Boolean)
    },
    loanSections () {
      const map = {}
      ;(this.assumptions?.loanPrepaymentRates || []).forEach(r => {
        if (!map[r.categoryCode]) {
          map[r.categoryCode] = { code: r.categoryCode, label: r.categoryLabel }
        }
      })
      const order = this.assumptions?.loanPrepaymentRateCategoryCodes || Object.keys(map)
      return order.map(code => map[code]).filter(Boolean)
    },
    depositSections () {
      const order = this.assumptions.depositDecayRateCategoryCodes || []
      const byCode = Object.fromEntries(
        (this.assumptions.depositDecayRates || []).map(d => [d.categoryCode, d])
      )
      return order
        .map(code => ({ code, label: byCode[code]?.categoryLabel || code }))
    },
    loanReinvestmentSections () {
      return (this.assumptions.loanReinvestmentSpreads || [])
        .map(s => ({ code: s.categoryCode, label: s.categoryLabel }))
    },
    depositReinvestmentSections () {
      return (this.assumptions.depositReinvestmentSpreads || [])
        .map(s => ({ code: s.categoryCode, label: s.categoryLabel }))
    }
  },
  watch: {
    loanModel: {
      handler (newVal) {
      },
      deep: true
    }
  },
  created () {
    this.getBankAssumptions()
  },
  methods: {
    async getBankAssumptions () {
      const analysisId = this.currentAnalysisId
      const liquidityId = this.currentLiquidityParametersId
      const response = await API.Resource.Assumptions.GetBankAssumptions(this.accountId, analysisId, liquidityId)
      this.handleResponse(response)
    },
    async saveAnalysisParameters (diff) {
      const analysisId = this.currentAnalysisId
      const response = await API.Resource.InterestRate.EditBankAnalysisAssumptions(this.accountId, analysisId, diff)

      this.handleResponse(response)
      return response
    },
    async editLiquidityAssumptions (liquidityAssumptions) {
      const response = await API.Resource.RiskLiquidity.EditLiquidityParameters(this.accountId, liquidityAssumptions)

      this.handleResponse(response)
      return response
    },

    handleResponse (response) {
      const data = API.responseData(response)?.bankAssumptions || null

      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response) && data) {
        this.$set(this.BankAssumptions, 'Data', data)
        if (this.currentAnalysisId == null) {
          this.currentAnalysisId = this.BankAssumptions.Data.analysisParameters.analysisId
        }
        this.initLoanModel()
        this.initDepositModel()
        this.initLoanReinvestmentModel()
        this.initDepositReinvestmentModel()
        this.initGeneralModel()
        this.initLiquidityModel()
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },
    initLoanModel () {
      const months = this.months
      const grouped = {}
      ;(this.assumptions?.loanPrepaymentRates || []).forEach(r => {
        grouped[r.categoryCode] = grouped[r.categoryCode] || {}
        grouped[r.categoryCode][r.month] = {
          rate   : Number(r.rate || 0),
          minRate: Number(r.minRate || 0),
          maxRate: Number(r.maxRate || 100)
        }
      })
      const codes = this.assumptions?.loanPrepaymentRateCategoryCodes || Object.keys(grouped)
      codes.forEach(code => {
        grouped[code] = grouped[code] || {}
        months.forEach(m => {
          if (!grouped[code][m]) {
            grouped[code][m] = { rate: 0, minRate: 0, maxRate: 100 }
          }
        })
      })
      this.loanModel = grouped
    },

    initDepositModel () {
      // shape the per-category fields for the component
      const out = {}
      ;(this.assumptions.depositDecayRates || []).forEach(d => {
        out[d.categoryCode] = {
          life       : d.life,
          minLife    : d.minLife,
          maxLife    : d.maxLife,
          rate       : d.rate,
          minRate    : d.minRate,
          maxRate    : d.maxRate,
          upBeta     : d.upBeta,
          minUpBeta  : d.minUpBeta,
          maxUpBeta  : d.maxUpBeta,
          downBeta   : d.downBeta,
          minDownBeta: d.minDownBeta,
          maxDownBeta: d.maxDownBeta
        }
      })
      this.depositModel = out
    },
    initLoanReinvestmentModel () {
      const out = {}
      ;(this.assumptions.loanReinvestmentSpreads || []).forEach(s => {
        out[s.categoryCode] = { spread: s.spread, minSpread: s.minSpread, maxSpread: s.maxSpread }
      })
      this.loanReinvestmentModel = out
    },
    initDepositReinvestmentModel () {
      const out = {}
      ;(this.assumptions.depositReinvestmentSpreads || []).forEach(s => {
        out[s.categoryCode] = {
          spread: s.spread, minSpread: s.minSpread, maxSpread: s.maxSpread
        }
      })

      this.depositReinvestmentModel = out
    },

    initGeneralModel () {
      const out = {}
      ;(this.assumptions?.generalAssumptions || []).forEach(cat => {
        const entry = {}
        ;(cat.categoryFields || []).forEach(f => {
          entry[f.code] = Number(f.value || 0)
        })
        out[cat.categoryCode] = entry
      })
      this.generalModel = out
    },
    initLiquidityModel () {
      const out = {}
      ;(this.liquidityAssumptions?.liquidityAssumptionItems || []).forEach(l => {
        out[l.code] = {
          type : l.type,
          title: l.title,
          value: l.value
        }
      })
      this.liquidityModel = out
    },
    onUpdateGeneral ({ categoryCode, fieldCode, value }) {
      if (!this.generalModel[categoryCode]) this.$set(this.generalModel, categoryCode, {})
      this.$set(this.generalModel[categoryCode], fieldCode, Number(value))
    },

    onUpdateLiquidity ({ fieldCode, value }) {
      if (!this.liquidityModel[fieldCode]) this.$set(this.liquidityModel, fieldCode, {})
      this.$set(this.liquidityModel[fieldCode], 'value', Number(value))
    },

    updateLoanRate ({ categoryCode, month, value }) {
      this.$set(this.loanModel[categoryCode][month], 'rate', value)
    },

    onUpdateDeposit ({ categoryCode, field, value }) {
      this.$set(this.depositModel[categoryCode], field, value)
    },

    onUpdateDepositReinvestment ({ categoryCode, field, value }) {
      this.$set(this.depositReinvestmentModel[categoryCode], field, value)
    },
    onUpdateLoanReinvestment ({ categoryCode, field, value }) {
      this.$set(this.loanReinvestmentModel[categoryCode], field, value)
    },

    changed  (a, b) {
      const EPS = 1e-6
      return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) > EPS
    },
    buildAnalysisParametersDiff (original, {
      loanModel,
      depositModel,
      reinvestmentModel,
      loanReinvestmentModel,
      generalModel
    }) {
      const params = {}

      const originalRates = original.loanPrepaymentRates || []
      const rateIndex = new Map(originalRates.map(r => [`${r.categoryCode}-${r.month}`, r.rate]))
      const lp = []
      Object.keys(loanModel || {}).forEach(code => {
        Object.keys(loanModel[code]).forEach(mStr => {
          const month = Number(mStr)
          const newRate = Number(loanModel[code][month]?.rate ?? NaN)
          const oldRate = rateIndex.has(`${code}-${month}`) ? rateIndex.get(`${code}-${month}`) : NaN
          if (this.changed(newRate, oldRate)) {
            lp.push({ categoryCode: code, month, rate: newRate })
          }
        })
      })
      if (lp.length) params.loanPrepaymentRates = lp

      const originalDecay = original.depositDecayRates || []
      const decayByCode = Object.fromEntries(originalDecay.map(d => [d.categoryCode, d]))
      const dd = []
      Object.keys(depositModel || {}).forEach(code => {
        const o = decayByCode[code] || {}
        const n = depositModel[code]
        const entry = { categoryCode: code }
        let touched = false

        if (this.changed(n.life, o.life)) { entry.life = Number(n.life); touched = true }
        if (this.changed(n.rate, o.rate)) { entry.rate = Number(n.rate); touched = true }
        if (this.changed(n.upBeta, o.upBeta)) { entry.upBeta = Number(n.upBeta); touched = true }
        if (this.changed(n.downBeta, o.downBeta)) { entry.downBeta = Number(n.downBeta); touched = true }

        if (touched) dd.push(entry)
      })
      if (dd.length) params.depositDecayRates = dd

      const originalDepReinv = original.depositReinvestmentSpreads || []
      const depReinvByCode = Object.fromEntries(originalDepReinv.map(s => [s.categoryCode, s.spread]))
      const dr = []
      Object.keys(reinvestmentModel || {}).forEach(code => {
        const newSpread = Number(reinvestmentModel[code]?.spread ?? NaN)
        const oldSpread = depReinvByCode[code]
        if (this.changed(newSpread, oldSpread)) {
          dr.push({ categoryCode: code, spread: newSpread })
        }
      })
      if (dr.length) params.depositReinvestmentSpreads = dr

      const originalLoanReinv = original.loanReinvestmentSpreads || []
      const loanReinvByCode = Object.fromEntries(originalLoanReinv.map(s => [s.categoryCode, s.spread]))
      const lr = []
      Object.keys(loanReinvestmentModel || {}).forEach(code => {
        const newSpread = Number(loanReinvestmentModel[code]?.spread ?? NaN)
        const oldSpread = loanReinvByCode[code]
        if (this.changed(newSpread, oldSpread)) {
          lr.push({ categoryCode: code, spread: newSpread })
        }
      })
      if (lr.length) params.loanReinvestmentSpreads = lr

      const originalGeneral = original.generalAssumptions || []
      // Map current values by category+field
      const originalIndex = new Map()
      originalGeneral.forEach(cat => {
        (cat.categoryFields || []).forEach(f => {
          originalIndex.set(`${cat.categoryCode}::${f.code}`, Number(f.value ?? NaN))
        })
      })

      const ga = []
      Object.keys(generalModel || {}).forEach(catCode => {
        const fields = generalModel[catCode] || {}
        Object.keys(fields).forEach(fieldCode => {
          const newVal = Number(fields[fieldCode] ?? NaN)
          const key = `${catCode}::${fieldCode}`
          const hasOld = originalIndex.has(key)
          const oldVal = hasOld ? originalIndex.get(key) : NaN

          // if old missing -> include whenever newVal is finite; else compare with EPS
          const isChanged = hasOld ? this.changed(newVal, oldVal) : Number.isFinite(newVal)

          if (isChanged) {
            ga.push({
              categoryCode: catCode,
              fieldCode,
              value       : `${newVal}`
            })
          }
        })
      })
      if (ga.length) params.generalAssumptions = ga

      return params
    },

    async rerunInterestRate () {
      if (this.isDraftAnalysis) {
        this.currentAnalysisId = this.previousAnalysisId
        this.previousAnalysisId = null
        this.$bus.$emit('nii:cancel-assumptions-edit', {})
      }
      this.openRerunModal()
      this.rerunProgress = 0
      this.rerunSuccess = false

      try {
        // Simulate progress updates
        this.rerunProgress = 25

        const defaultPayload = {
          loanPrepaymentRates       : [],
          depositDecayRates         : [],
          depositReinvestmentSpreads: [],
          loanReinvestmentSpreads   : [],
          generalAssumptions        : []
        }

        this.rerunProgress = 50

        const diff = this.buildAnalysisParametersDiff(this.assumptions, {
          loanModel            : this.loanModel,
          depositModel         : this.depositModel,
          reinvestmentModel    : this.depositReinvestmentModel,
          loanReinvestmentModel: this.loanReinvestmentModel,
          generalModel         : this.generalModel
        })

        this.rerunProgress = 75

        const rerunRes = await this.saveAnalysisParameters({
          ...defaultPayload,
          ...diff
        })

        this.reRunAnalysisId = rerunRes.data.data.editBankAnalysisAssumptions

        this.rerunProgress = 100

        // Show success state after a brief delay
        setTimeout(() => {
          this.rerunSuccess = true
        }, 1000)
      } catch (error) {
        this.$bus.$emit('app:error', [error.message || 'An error occurred during rerun'])
        this.closeRerunModal()
      }
    },

    async rerunLiquidity () {
      this.openRerunModal()
      this.rerunProgress = 0
      this.rerunSuccess = false

      try {
        // Simulate progress updates
        this.rerunProgress = 25
        this.rerunProgress = 50
        this.rerunProgress = 75

        const payload = {
          liquidityAssumptionItems: (Object.entries(this.liquidityModel) || {}).map(([k, v], index) => ({
            value: v.value, code: k
          }))
        }
        const rerunRes = await this.editLiquidityAssumptions(payload)

        this.reRunLiquidityId = rerunRes.data.data.editLiquidityParameters

        this.rerunProgress = 100
        this.$bus.$emit('liquidity: rerun', {})
        // Show success state after a brief delay
        setTimeout(() => {
          this.rerunSuccess = true
        }, 1000)
      } catch (error) {
        this.$bus.$emit('app:error', [error.message || 'An error occurred during rerun'])
        this.closeRerunModal()
      }
    },

    openRerunModal () {
      this.showRerunModal = true
    },
    closeRerunModal () {
      this.showRerunModal = false
      this.rerunProgress = 0
      this.rerunSuccess = false
    },
    handleCancel () {
      // Navigate back to the previous page
      const intra = this.$route.query.intra
      if (intra) {
        this.$router.push({
          name  : intra,
          params: {
            tab: this.$route.query.from
          }
        })
      } else {
        // Fallback: go back in history
        this.$router.go(-1)
      }
    },
    onSuccessLinkClick () {
      if (this.tab === 0 || this.tab === 1 || this.tab === 2) {
        this.isDraftAnalysis = true
        this.previousAnalysisId = this.currentAnalysisId
        this.currentAnalysisId = this.reRunAnalysisId
      } else if (this.tab === 3) {
        this.isDraftLiquidity = true
        this.currentLiquidityParametersId = this.reRunLiquidityId
      } else {
        if (!this.previousAnalysisId) {
          this.previousAnalysisId = this.reRunAnalysisId
        } else {
          this.previousAnalysisId = this.currentAnalysisId
        }
        this.currentAnalysisId = this.reRunAnalysisId
        this.currentLiquidityParametersId = this.reRunLiquidityId
      }

      const linkText = this.successLinkText
      let route = null

      const intra = this.$route.query.intra

      switch (linkText) {
        case this.$t('EVE.Tab.InternalThresholds.Title') & intra === 'EVE':
          route = { name: 'EVE', params: { tab: 'internal-thresholds' } }
          break
        case this.$t('EVE.Tab.BaselTests.Title'):
          route = { name: 'EVE', params: { tab: 'basel-tests' } }
          break
        case this.$t('EVE.Tab.HistoricalEVEChanges.Title'):
          route = { name: 'EVE', params: { tab: 'historical-eve-changes' } }
          break
        case this.$t('EVE.Tab.SharpestEVEChanges.Title'):
          route = { name: 'EVE', params: { tab: 'sharpest-eve-changes' } }
          break
        case this.$t('NII.Tab.SynthesisRisk.Title'):
          route = { name: 'NII', params: { tab: 'synthesis-risk' } }
          break
        case this.$t('NII.Tab.InternalThresholds.Title') & intra === 'NII':
          route = { name: 'NII', params: { tab: 'internal-thresholds' } }
          break
        case this.$t('EVE.Tab.RegulatoryThresholds.Title'):
          route = { name: 'EVE', params: { tab: 'regulatory-thresholds' } }
          break
        case this.$t('RiskLiquidity.Tab.DeferenceReadiness.Title'):
          route = { name: 'RiskLiquidity', params: { tab: 'defence-readiness' } }
          break
        case this.$t('RiskLiquidity.Tab.BankFailures.Title'):
          route = { name: 'RiskLiquidity', params: { tab: 'bank-failures' } }
          break
        case this.$t('RiskLiquidity.Tab.BankRunBenchmarking.Title'):
          route = { name: 'RiskLiquidity', params: { tab: 'bank-run-benchmarking' } }
          break
        case this.$t('RiskLiquidity.Tab.Monitoring.Title'):
          route = { name: 'RiskLiquidity', params: { tab: 'monitoring' } }
          break
        default:
          route = {
            name  : intra,
            params: {
              tab: this.$route.query.from
            }
          }
      }

      this.$router.push(route)
      this.closeRerunModal()
    },

    getEditedPayload () {
      const loans = []
      Object.keys(this.loanModel).forEach(code => {
        this.months.forEach(m => {
          const r = this.loanModel[code][m]
          loans.push({
            categoryCode: code,
            month       : Number(m),
            rate        : Number(r.rate),
            minRate     : Number(r.minRate),
            maxRate     : Number(r.maxRate)
          })
        })
      })

      const deposits = Object.keys(this.depositModel).map(code => {
        const d = this.depositModel[code]
        return {
          categoryCode: code,
          life        : d.life,
          minLife     : d.minLife,
          maxLife     : d.maxLife,
          rate        : d.rate,
          minRate     : d.minRate,
          maxRate     : d.maxRate,
          upBeta      : d.upBeta,
          minUpBeta   : d.minUpBeta,
          maxUpBeta   : d.maxUpBeta,
          downBeta    : d.downBeta,
          minDownBeta : d.minDownBeta,
          maxDownBeta : d.maxDownBeta
        }
      })

      const general = []
      ;(this.generalSections || []).forEach(sec => {
        (sec.fields || []).forEach(f => {
          const v = Number(this.generalModel?.[sec.code]?.[f.code] ?? NaN)
          if (Number.isFinite(v)) {
            general.push({
              categoryCode: sec.code,
              fieldCode   : f.code,
              value       : `${v}`
            })
          }
        })
      })

      return {
        loanPrepaymentRates: loans,
        depositDecayRates  : deposits,
        generalAssumptions : general

      }
    }

  }
}
  </script>

  <style scoped>
  .assumption-card{
    border: 1px solid #E6EEF5;
    background: #FFFFFF;
    border-radius: 12px;
    padding: 16px;
  }
  .assumption-title{
    font-size: 14px;
    font-weight: 600;
    color: #63747D;
    margin-bottom: 10px;
  }
  .row-title{
    font-size: 13px;
    font-weight: 600;
    color: #041F30;
    margin-bottom: 4px;
  }
  .row-subtitle{
    font-size: 11px;
    color: #7D8A92;
    margin-top: -2px;
  }
  .row-controls{
    display: flex;
    align-items: center;
  }
  .pct-input{
    max-width: 110px;
  }
  .cancel-btn {
    background-color: var(--v-synthesis-ui-grey-04-base) !important;
    color: var(--v-synthesis-brand-dark-blue-base) !important;
  }
  </style>
